import { useFeatureIsOn } from '@growthbook/growthbook-react'
import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Spin, List, Row, Col, Card, Space, Tooltip } from 'antd'
import { Typography } from 'modules/core/components'
import { isUserAllowed, getOperationsByKeys, sendUserDataToGTM } from 'modules/core/utils'
import { ROLES } from 'modules/core/constants'
import { SiderLayout } from 'modules/core/layouts'
import { InfoCircleOutlined } from '@ant-design/icons'
import configuration from 'modules/configuration'
import login from 'modules/login'
import reports from 'modules/reports'

import './Configuration.scss'
import { ConfigSkeleton } from './components'
import { listDataConfigModules } from './listDataConfigModules'
import planning from 'modules/planning'
import PropTypes from 'prop-types'

const {
  SETTINGS__BUDGET_TYPE,
  SETTINGS__DIMENSIONS,
  SETTINGS__DIMENSION_MAPPINGS,
  SETTINGS__REPORTS,
} = ROLES

const FRONT_URL = process.env.REACT_APP_FRONT_URL || 'http://localhost:3030'

const Configuration = ({
  dimensionsList,
  dimensionMappingsList,
  budgetTypeList,
  fetchDimensionsList,
  fetchDimensionMappings,
  fetchBudgetTypeList,
  fetchReportList,
  reportList,
  modifiersList,
  loggedUser,
  fetchPeriodList,
  periodList,
}) => {
  const enabled = true
  const [isScreenLoading, setIsScreenLoading] = useState(true)
  const { t } = useTranslation()
  let history = useHistory()

  // Revamp configuration
  const ab_revamp_configuration = useFeatureIsOn('ab_revamp_configuration')
  const [loadingIframe, setLoadingIframe] = useState(true)

  useEffect(() => {
    setIsScreenLoading(true)
    const isFetcheable = (rolesArray) => isUserAllowed(getOperationsByKeys(rolesArray))

    Promise.all([
      isFetcheable([SETTINGS__BUDGET_TYPE]) && fetchBudgetTypeList(),
      isFetcheable([SETTINGS__REPORTS]) && fetchReportList(),
      isFetcheable([SETTINGS__DIMENSIONS]) && fetchDimensionsList(),
      isFetcheable([SETTINGS__DIMENSION_MAPPINGS]) && fetchDimensionMappings(),
      fetchPeriodList(),
    ])
      .then(() => setIsScreenLoading(false))
      .catch(() => setIsScreenLoading(false))

    return () => setIsScreenLoading(false)
  }, [
    fetchDimensionsList,
    fetchDimensionMappings,
    fetchBudgetTypeList,
    fetchReportList,
    fetchPeriodList,
  ])

  useEffect(() => {
    sendUserDataToGTM(loggedUser, 'Configuración', 'Ingresar configuración', 'Visualización')
  }, [loggedUser])

  let modifiersListValue = modifiersList
  if (!enabled) {
    modifiersListValue = {
      ...modifiersList,
      values: [{ name: 'LABEL_BUSINESS_RULES', key: 'rules' }],
    }
  }

  const params = {
    dimensionsList,
    dimensionMappingsList,
    budgetTypeList,
    reportList,
    modifiersList: modifiersListValue,
    history,
    enabledWorkflow: false,
    periodList,
  }

  const renderHeader = () => (
    <Typography.Headline level={5}>{t('LABEL_CONFIG')}</Typography.Headline>
  )

  const renderCard = (item) => {
    return (
      <Card
        className="card-config"
        onClick={item.url ? () => history.push(item.url) : null}
        onMouseDown={(e) => {
          if (e.button === 1) {
            e.stopPropagation()
            window.open(item.url, '_blank')
          }
        }}
      >
        <Row gutter={[24, 24]}>
          <Col span={24}>{item.icon}</Col>
          <Col />
          <Col span={24}>
            <Space direction="vertical">
              <Space>
                <Typography.Headline type="primary" level={5}>
                  {item.title}
                </Typography.Headline>
                {item.tooltipText && (
                  <Tooltip title={item.tooltipText}>
                    <InfoCircleOutlined />
                  </Tooltip>
                )}
              </Space>
              <Typography.Body level={3}>{`${item.count} ${
                item.count === 1 ? 'elemento' : 'elementos'
              }`}</Typography.Body>
            </Space>
          </Col>
        </Row>
      </Card>
    )
  }

  const renderList = () => (
    <List
      grid={{ gutter: 24, column: 4 }}
      dataSource={listDataConfigModules(params).filter((el) =>
        isUserAllowed(getOperationsByKeys(el.allowedFor)),
      )}
      renderItem={(item) => <List.Item key={item.key}>{renderCard(item)}</List.Item>}
    />
  )

  const handleLoad = () => {
    console.log('Iframe loaded')
    setLoadingIframe(false)
  }

  if (ab_revamp_configuration) {
    return (
      <SiderLayout className={'dashboard-home-iframe'}>
        {loadingIframe && <ConfigSkeleton />}
        <iframe
          id="DashboardhHome"
          title="DashboardhHome"
          src={`${FRONT_URL}/iframe/configuration`}
          width="100%"
          height="100%"
          style={{
            border: 'none',
            padding: '0 0',
            overflowX: 'hidden',
            overflowY: 'auto',
          }}
          onLoad={handleLoad}
        ></iframe>
      </SiderLayout>
    )
  }

  return (
    <SiderLayout>
      <div className="configuration-container">
        <Row gutter={[24, 24]}>
          <Col span={24}>{renderHeader()}</Col>
          <Col span={24}>
            <Spin spinning={isScreenLoading} />
            {!isScreenLoading && renderList()}
          </Col>
        </Row>
      </div>
    </SiderLayout>
  )
}

const mapStateToProps = (state) => ({
  dimensionsList: configuration.selectors.getDimensionsList(state),
  dimensionMappingsList: configuration.selectors.getDimensionMappingsList(state),
  budgetTypeList: configuration.selectors.getBudgetTypeList(state),
  reportList: reports.selectors.getReportList(state),
  modifiersList: configuration.selectors.getModifiersList(state),
  loggedUser: login.selectors.getWhoAmI(state),
  periodList: planning.selectors.getPeriodList(state),
})

const mapDispatchToProps = {
  fetchDimensionsList: configuration.actions.fetchDimensionsList,
  fetchDimensionMappings: configuration.actions.fetchDimensionMappings,
  fetchBudgetTypeList: configuration.actions.fetchBudgetTypeList,
  fetchReportList: reports.actions.fetchReportList,
  fetchPeriodList: planning.actions.fetchPeriodList,
}

Configuration.propTypes = {
  loggedUser: PropTypes.object,
  fetchPeriodList: PropTypes.func,
  periodList: PropTypes.array,
}

export default connect(mapStateToProps, mapDispatchToProps)(Configuration)
